'use client';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import { SearchButton } from '@eliadmin/core/src/components/header/search-button';
import { deferOverlayComponent } from '@eliadmin/core/src/components/ui/dialog';
import { createContextStore } from '@eliadmin/core/src/stores/context-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { ChevronDown as LuChevronDown, ChevronUp as LuChevronUp } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { useEffect, useId } from 'react';
import type { MenuMobileContent } from './menu-mobile-content';

const [MobileMenuProvider, useMobileMenu] = createContextStore<
  { isOpen: boolean },
  { toggle: () => void }
>(
  { isOpen: false },
  (set, get) => ({ toggle: () => set({ isOpen: !get().isOpen }) }),
);

export type MobileMenuProps = {
  className?: {
    root?: string;
    content?: string;
    closeButton?: string;
    categories?: string;
  };
  categories?: CategoriesEntity[];
};

const LazyMenuMobile = deferOverlayComponent<typeof MenuMobileContent>(
  () => import('./menu-mobile-content').then(runtime => runtime.MenuMobileContent),
  'MenuMobileContent',
);

function MenuMobile(props: MobileMenuProps) {
  const isOpen = useMobileMenu(s => s.isOpen);
  return isOpen ? <LazyMenuMobile {...props} /> : null;
}

function MobileMenuButton({
  className,
  categories,
}: {
  className?: { root?: string; button?: string };
  categories: CategoriesEntity[];
}) {
  const { toggle, isOpen } = useMobileMenu();
  const pathname = usePathname();
  const menuLabelId = useId();
  const menuContentId = useId();

  useEffect(() => {
    if (isOpen) {
      toggle();
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }
  }, [pathname]);

  return (
    <div
      className={cn('ml-0 block items-center lg:hidden', 'ea-mobile-menu-button', className?.root)}
      style={{ order: 7 }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <button
            type="button"
            aria-labelledby={menuLabelId}
            aria-expanded={isOpen}
            aria-controls={isOpen ? menuContentId : undefined}
            className={cn(
              'flex h-10 w-10 cursor-pointer items-center justify-center',
              'ea-mobile-menu-button__button',
              className?.button,
            )}
            onClick={toggle}
          >
            {isOpen ? (
              <LuChevronUp className={'h-5 w-5'} />
            ) : (
              <LuChevronDown className={'h-5 w-5'} />
            )}
          </button>
          <button type="button" id={menuLabelId} aria-expanded={isOpen} aria-controls={isOpen ? menuContentId : undefined} onClick={toggle}>
            Kategórie
          </button>
        </div>
        <div>
          <SearchButton variant={'ghost'} />
        </div>
      </div>
      {isOpen && (
        <div id={menuContentId} className="w-full bg-primary">
          <MenuMobile categories={categories} />
        </div>
      )}
    </div>
  );
}

export { MenuMobile, MobileMenuButton, MobileMenuProvider, useMobileMenu };
